import { useCallback, useMemo, useRef } from "react";
import Dropdown from "../../../../../../components/Dropdown/Dropdown";
import InboxEmailCard from "./components/InboxEmailCard";
import InboxEmailCardShimmer from "./components/InboxEmailCardShimmer";
import type { InboxThread } from "../../utils/types";
import PulseLoader from "../../../../../../components/Loader/PulseLoader";

const DEFAULT_FILTER_OPTIONS = [
  { value: "all", label: "All Threads", count: 0 },
];

interface FilterOption {
  label: string;
  value: string;
  count: number;
}

const buildDropdownOptions = (options: FilterOption[]) =>
  options.map((option) => ({
    value: option.value,
    label: option.label,
    selectedOptionContent: option.label,
    content: (
      <span className="flex items-center gap-[6px]">
        <span>{option.label}</span>
        <span className="text-xxs font-normal text-dark-gray dark:text-dark-gray-dk">
          ({option.count})
        </span>
      </span>
    ),
  }));

interface InboxEmailListProps {
  threads: InboxThread[];
  selectedThreadId: string;
  isLoading: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  selectedFilter: string;
  filterOptions: FilterOption[];
  onFilterChange: (value: string) => void;
  onThreadSelect: (thread: InboxThread) => void;
  onLoadMore: () => void;
}

const InboxEmailList: React.FC<InboxEmailListProps> = ({
  threads,
  selectedThreadId,
  isLoading,
  hasMore,
  isLoadingMore,
  selectedFilter,
  filterOptions,
  onFilterChange,
  onThreadSelect,
  onLoadMore,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const rawOptions =
    filterOptions?.length > 0 ? filterOptions : DEFAULT_FILTER_OPTIONS;
  const dropdownOptions = useMemo(
    () => buildDropdownOptions(rawOptions),
    [rawOptions]
  );

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !hasMore || isLoadingMore) return;

    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    if (nearBottom) {
      onLoadMore();
    }
  }, [hasMore, isLoadingMore, onLoadMore]);

  const activeFilterCount =
    rawOptions?.find((o) => o?.value === selectedFilter)?.count ||
    threads?.length ||
    0;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-[12px] h-[50px] flex items-center justify-between border-b border-border-gray dark:border-border-gray-dk shrink-0">
        <Dropdown
          id="inbox-filter-dropdown"
          options={dropdownOptions}
          value={selectedFilter}
          onChange={onFilterChange}
          ariaLabel="select inbox filter"
          className="min-w-fit max-w-fit"
          dropdownClassName="min-w-max mt-1 max-h-fit"
          selectedOptionContainerClassName="pl-[8px] pr-[8px] pt-[6px] pb-[6px] !min-w-[148px]"
          optionClassName="pl-[10px] pr-[10px] !w-[180px]"
          checkIconClassName="!text-verba-blue !dark:text-verba-blue-dk !w-[18px] !h-[18px]"
        />
        <p className="text-md font-medium text-dark-gray dark:text-dark-gray-dk">
          {activeFilterCount} {""}
          Threads
        </p>
      </div>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto min-h-0"
      >
        {isLoading && threads?.length === 0 ? (
          <div className="flex flex-col">
            {[...Array(8)].map((_, index) => (
              <InboxEmailCardShimmer key={index} />
            ))}
          </div>
        ) : threads?.length === 0 ? (
          <div className="flex items-center justify-center h-full text-dark-gray dark:text-dark-gray-dk text-md">
            No threads found
          </div>
        ) : (
          <>
            {threads?.map((thread) => (
              <InboxEmailCard
                key={thread?.thread_id}
                thread={thread}
                isSelected={selectedThreadId === thread?.thread_id}
                onClick={() => onThreadSelect(thread)}
              />
            ))}
            {isLoadingMore && (
              <div className="flex items-center justify-center py-4">
                <div className="flex items-center gap-2 text-sm text-dark-gray dark:text-dark-gray-dk">
                  <PulseLoader size={6} />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InboxEmailList;
